<!-- Global Upload Status Bar - survives navigation -->
<div
	class="upload-status-bar"
	*ngIf="uploadState$ | async as state"
	[class.visible]="state.items.length > 0"
	[class.expanded]="isExpanded"
	role="region"
	aria-label="Upload progress"
	i18n-aria-label="Accessibility Label@@globalUploadStatusBarUploadProgressAccessibilityLabel"
>
	<div class="status-bar-content">
		<!-- Header / Summary Row -->
		<div class="status-header" (click)="toggleExpanded()">
			<div class="progress-summary">
				<mat-icon class="upload-icon">cloud_upload</mat-icon>

				<ng-container *ngIf="hasActiveUploads$ | async; else completedSummary">
					<span class="file-count" i18n="Text@@globalUploadStatusBarUploading"> {{ uploadingCount$ | async }} uploading </span>
					<span class="speed-badge" *ngIf="currentSpeed > 0">{{ formatSpeed(currentSpeed) }}</span>
					<span class="eta" *ngIf="estimatedTimeRemaining" i18n="Text@@globalUploadStatusBarLeft"
						>{{ estimatedTimeRemaining }} left</span
					>
				</ng-container>

				<ng-template #completedSummary>
					<span class="file-count completed">
						<ng-container *ngIf="completedCount$ | async as completed" i18n="Text@@globalUploadStatusBarCompleted">
							{{ completed }} completed
						</ng-container>
						<ng-container *ngIf="failedCount$ | async as failed">
							<span *ngIf="failed > 0" class="failed-count" i18n="Text@@globalUploadStatusBarFailed"
								>, {{ failed }} failed</span
							>
						</ng-container>
					</span>
				</ng-template>
			</div>

			<div class="header-actions">
				<button
					mat-icon-button
					class="expand-toggle"
					[attr.aria-expanded]="isExpanded"
					aria-label="Toggle file list"
					i18n-aria-label="Accessibility Label@@globalUploadStatusBarToggleFileListAccessibilityLabel"
				>
					<mat-icon>{{ isExpanded ? 'expand_more' : 'expand_less' }}</mat-icon>
				</button>
			</div>
		</div>

		<!-- Progress Bar (always visible) -->
		<div class="progress-bar-container">
			<div
				class="progress-bar-fill"
				[style.width.%]="overallProgress$ | async"
				[class.complete]="(hasActiveUploads$ | async) === false"
				role="progressbar"
				[attr.aria-valuenow]="overallProgress$ | async"
				aria-valuemin="0"
				aria-valuemax="100"
			></div>
		</div>

		<!-- Expanded File List -->
		<div class="file-list-container" *ngIf="isExpanded">
			<!-- Regular list for <= 20 files -->
			<div class="file-list" *ngIf="state.items.length <= 20">
				<div *ngFor="let item of state.items; trackBy: trackByItemId" class="file-item" [class]="getStatusClass(item)">
					<mat-icon class="status-icon" [class]="getStatusClass(item)">
						{{ getStatusIcon(item) }}
					</mat-icon>

					<div class="file-info">
						<span class="filename" [title]="item.file.name">{{ item.file.name }}</span>
						<span class="file-meta">
							{{ formatFileSize(item.progress?.totalBytes) }}
							<span *ngIf="item.directoryId" class="folder-indicator" i18n="Text@@globalUploadStatusBarFolder">
								in folder
							</span>
						</span>
					</div>

					<div class="item-progress" *ngIf="item.status === 'uploading' || item.status === 'pending'">
						<span class="percent">{{ item.progress?.percentage || 0 }}%</span>
						<div class="mini-progress-bar">
							<div class="mini-progress-fill" [style.width.%]="item.progress?.percentage || 0"></div>
						</div>
					</div>

					<div class="item-actions">
						<button
							mat-icon-button
							class="cancel-btn"
							*ngIf="item.status === 'uploading' || item.status === 'pending'"
							(click)="cancelUpload(item.id); $event.stopPropagation()"
							aria-label="Cancel upload"
							i18n-aria-label="Accessibility Label@@globalUploadStatusBarCancelUploadAccessibilityLabel"
						>
							<mat-icon>close</mat-icon>
						</button>
						<button
							mat-icon-button
							class="retry-btn"
							*ngIf="item.status === 'error'"
							(click)="retryUpload(item.id); $event.stopPropagation()"
							aria-label="Retry upload"
							i18n-aria-label="Accessibility Label@@globalUploadStatusBarRetryUploadAccessibilityLabel"
						>
							<mat-icon>refresh</mat-icon>
						</button>
					</div>
				</div>
			</div>

			<!-- Virtual scroll for > 20 files -->
			<cdk-virtual-scroll-viewport itemSize="48" class="file-list-virtual" *ngIf="state.items.length > 20">
				<div *cdkVirtualFor="let item of state.items; trackBy: trackByItemId" class="file-item" [class]="getStatusClass(item)">
					<mat-icon class="status-icon" [class]="getStatusClass(item)">
						{{ getStatusIcon(item) }}
					</mat-icon>

					<div class="file-info">
						<span class="filename" [title]="item.file.name">{{ item.file.name }}</span>
						<span class="file-meta">
							{{ formatFileSize(item.progress?.totalBytes) }}
						</span>
					</div>

					<div class="item-progress" *ngIf="item.status === 'uploading' || item.status === 'pending'">
						<span class="percent">{{ item.progress?.percentage || 0 }}%</span>
					</div>

					<div class="item-actions">
						<button
							mat-icon-button
							class="cancel-btn"
							*ngIf="item.status === 'uploading' || item.status === 'pending'"
							(click)="cancelUpload(item.id); $event.stopPropagation()"
							aria-label="Cancel upload"
							i18n-aria-label="Accessibility Label@@globalUploadStatusBarCancelUploadAccessibilityLabel2"
						>
							<mat-icon>close</mat-icon>
						</button>
						<button
							mat-icon-button
							class="retry-btn"
							*ngIf="item.status === 'error'"
							(click)="retryUpload(item.id); $event.stopPropagation()"
							aria-label="Retry upload"
							i18n-aria-label="Accessibility Label@@globalUploadStatusBarRetryUploadAccessibilityLabel2"
						>
							<mat-icon>refresh</mat-icon>
						</button>
					</div>
				</div>
			</cdk-virtual-scroll-viewport>
		</div>

		<!-- Action Buttons -->
		<div class="actions" *ngIf="isExpanded">
			<button
				mat-button
				class="action-btn"
				(click)="cancelAll()"
				*ngIf="hasActiveUploads$ | async"
				i18n="Button Label@@globalUploadStatusBarCancelAllButtonLabel"
			>
				Cancel All
			</button>
			<button
				mat-button
				class="action-btn"
				(click)="retryAllFailed()"
				*ngIf="hasFailedUploads$ | async"
				i18n="Button Label@@globalUploadStatusBarRetryFailedButtonLabel"
			>
				Retry Failed
			</button>
			<button
				mat-button
				class="action-btn"
				(click)="clearCompleted()"
				*ngIf="completedCount$ | async"
				i18n="Button Label@@globalUploadStatusBarClearCompletedButtonLabel"
			>
				Clear Completed
			</button>
			<button
				mat-button
				color="primary"
				class="action-btn dismiss-btn"
				(click)="dismiss()"
				i18n="Button Label@@globalUploadStatusBarDismissButtonLabel"
			>
				Dismiss
			</button>
		</div>
	</div>
</div>
